/* global DOMParser, Event */
import { Controller } from '@hotwired/stimulus'
import { parse, format } from 'date-fns'
import FlatpickrManager from '../utils/flatpickr_manager'
import { FilterUrlBuilder } from '../utils/filter_url_builder'

export default class ParkingSessionFiltersController extends Controller {
  static targets = [
    'sessionType',
    'query',
    'dateRange',
    'dateRangeContainer',
    'timeSections',
    'fromHour',
    'fromMinute',
    'fromAmPm',
    'toHour',
    'toMinute',
    'toAmPm'
  ]

  static values = {
    locationId: Number
  }

  static DEBOUNCE_DELAY = 300
  static REINIT_DELAY = 50
  static FLATPICKR_REINIT_DELAY = 150
  static LOADING_OPACITY = '0.6'
  static DEFAULT_TAB_TYPE = 'ongoing'

  static SELECTORS = {
    activeTab: '.event-tab.active',
    sessionsContainer: 'sessions-container'
  }

  static TURBO_ACTIONS = {
    replace: 'replace',
    update: 'update',
    append: 'append',
    prepend: 'prepend',
    remove: 'remove'
  }

  connect () {
    this.debounceTimeout = null
    this.lastFilterUrl = null
    this.padExistingMinuteValues()
    this.initializeFlatpickr()
  }

  disconnect () {
    this.clearDebounceTimeout()
    this.destroyFlatpickr()
  }

  initializeFlatpickr () {
    if (!this.hasDateRangeTarget || this.flatpickrManager) return

    const container = this.hasDateRangeContainerTarget ? this.dateRangeContainerTarget : null
    const timeSections = this.hasTimeSectionsTarget ? this.timeSectionsTarget : null

    this.flatpickrManager = new FlatpickrManager(this.dateRangeTarget, {
      appendTo: container,
      timeSectionsElement: timeSections,
      dateRangeContainerElement: container
    })

    this.flatpickrManager.initialize(
      () => this.performFilter(),
      null,
      null
    )
  }

  destroyFlatpickr () {
    if (this.flatpickrManager) {
      this.flatpickrManager.destroy()
      this.flatpickrManager = null
    }
  }

  reinitializeFlatpickr () {
    if (!this.flatpickrManager) {
      this.initializeFlatpickr()
      return
    }

    this.flatpickrManager.reinitialize(
      () => this.performFilter(),
      null,
      null
    )

    setTimeout(() => {
      this.restoreFlatpickrDateRange()
    }, this.constructor.FLATPICKR_REINIT_DELAY)
  }

  restoreFlatpickrDateRange () {
    if (!this.hasDateRangeTarget || !this.dateRangeTarget.value) return
    if (!this.flatpickrManager?.getInstance()) return

    const dateRangeValue = this.dateRangeTarget.value
    const dates = dateRangeValue.split(/\s+to\s+/i)

    if (dates.length === 0) return

    try {
      const startDate = dates[0]
      const endDate = dates[1] || dates[0]
      const start = new Date(startDate + 'T00:00:00')
      const end = new Date(endDate + 'T23:59:59')

      if (!isNaN(start.getTime()) && !isNaN(end.getTime())) {
        this.flatpickrManager.getInstance().setDate([start, end], false)
      }
    } catch (e) {
    }
  }

  padExistingMinuteValues () {
    if (this.hasFromMinuteTarget && this.fromMinuteTarget.value) {
      const value = parseInt(this.fromMinuteTarget.value, 10)
      if (!isNaN(value)) {
        this.fromMinuteTarget.value = value.toString().padStart(2, '0')
      }
    }

    if (this.hasToMinuteTarget && this.toMinuteTarget.value) {
      const value = parseInt(this.toMinuteTarget.value, 10)
      if (!isNaN(value)) {
        this.toMinuteTarget.value = value.toString().padStart(2, '0')
      }
    }
  }

  validateTime (event) {
    const input = event.target
    const targetName = input.dataset.parkingSessionFiltersTarget

    if (!targetName) return

    const currentValue = input.value.trim()
    let value = parseInt(currentValue, 10)

    if (isNaN(value)) {
      value = targetName.includes('Hour') ? 12 : 0
    }

    if (targetName.includes('Hour')) {
      value = Math.max(1, Math.min(12, value))
    } else if (targetName.includes('Minute')) {
      value = Math.max(0, Math.min(59, value))
    } else {
      return
    }

    input.value = value.toString().padStart(2, '0')
  }

  updateTime () {
  }

  filter (event) {
    if (this.shouldDebounce(event)) {
      this.debounceFilter()
      return
    }

    this.performFilter()
  }

  shouldDebounce (event) {
    return this.hasQueryTarget && event?.type === 'input'
  }

  debounceFilter () {
    this.clearDebounceTimeout()
    this.debounceTimeout = setTimeout(() => {
      this.performFilter()
    }, this.constructor.DEBOUNCE_DELAY)
  }

  clearDebounceTimeout () {
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout)
      this.debounceTimeout = null
    }
  }

  performFilter () {
    const locationId = this.getLocationId()
    if (!locationId) {
      console.warn('Location ID not found')
      return
    }

    const filterParams = this.getFilterParams()
    const url = FilterUrlBuilder.build(locationId, filterParams)

    this.lastFilterUrl = url
    this.showLoading()

    this.fetchFilteredSessions(url, filterParams.tabType)
      .catch(error => {
        console.error('Error filtering sessions:', error)
        this.hideLoading()
      })
  }

  getFilterParams () {
    const params = {
      sessionType: this.getTargetValue('sessionType'),
      query: this.getTargetValue('query', true),
      dateRange: this.getTargetValue('dateRange'),
      tabType: this.getActiveTabType(),
      perPage: this.getPerPageValue()
    }

    if (this.hasTimeTargets('from')) {
      params.fromTime = this.buildTimeString('from')
    }

    if (this.hasTimeTargets('to')) {
      params.toTime = this.buildTimeString('to')
    }

    return params
  }

  hasTimeTargets (prefix) {
    const capitalized = prefix.charAt(0).toUpperCase() + prefix.slice(1)
    return (
      this[`has${capitalized}HourTarget`] &&
      this[`has${capitalized}MinuteTarget`] &&
      this[`has${capitalized}AmPmTarget`]
    )
  }

  buildTimeString (prefix) {
    const hour = this.getTargetValue(`${prefix}Hour`)
    const minute = this.getTargetValue(`${prefix}Minute`)
    const ampm = this.getTargetValue(`${prefix}AmPm`)

    if (!hour || !minute || !ampm) return ''

    try {
      const timeStr = `${hour}:${String(minute).padStart(2, '0')} ${ampm}`
      const parsed = parse(timeStr, 'h:mm a', new Date())
      return isNaN(parsed.getTime()) ? '' : format(parsed, 'HH:mm')
    } catch {
      return ''
    }
  }

  getTargetValue (targetName, shouldTrim = false) {
    const capitalized = targetName.charAt(0).toUpperCase() + targetName.slice(1)
    const hasTargetKey = `has${capitalized}Target`
    const targetKey = `${targetName}Target`

    if (!this[hasTargetKey]) return ''

    const value = this[targetKey]?.value || ''
    return shouldTrim ? value.trim() : value
  }

  getActiveTabType () {
    const activeTab = document.querySelector(this.constructor.SELECTORS.activeTab)
    return activeTab?.dataset?.tabType || this.constructor.DEFAULT_TAB_TYPE
  }

  getLocationId () {
    if (this.locationIdValue) {
      return this.locationIdValue
    }

    const container = document.getElementById(this.constructor.SELECTORS.sessionsContainer)
    return container?.dataset?.locationId || container?.getAttribute('data-location-id')
  }

  getPerPageValue () {
    const paginationElement = document.querySelector('[data-controller*="pagination"]')
    if (paginationElement) {
      const application = window.Stimulus || this.application
      const paginationController = application.getControllerForElementAndIdentifier(
        paginationElement,
        'pagination'
      )
      if (paginationController && paginationController.perPageValue) {
        return paginationController.perPageValue.toString()
      }
    }

    const perPageSelect = document.querySelector('[data-pagination-target="perPageSelect"]')
    if (perPageSelect && perPageSelect.value) {
      return perPageSelect.value
    }

    return '10'
  }

  async fetchFilteredSessions (url, tabType) {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Accept: 'text/vnd.turbo-stream.html, text/html',
        'X-Requested-With': 'XMLHttpRequest'
      },
      credentials: 'same-origin'
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const contentType = response.headers.get('Content-Type')
    const html = await response.text()

    if (this.isTurboStreamResponse(contentType)) {
      this.processTurboStream(html)
    } else {
      this.updateContent(html, tabType)
    }

    this.hideLoading()
  }

  isTurboStreamResponse (contentType) {
    return contentType?.includes('text/vnd.turbo-stream.html')
  }

  processTurboStream (html) {
    const parser = new DOMParser()
    const doc = parser.parseFromString(html, 'text/html')
    const turboStreams = doc.querySelectorAll('turbo-stream')

    turboStreams.forEach(stream => this.applyTurboStreamAction(stream))

    setTimeout(() => {
      this.reinitializeControllers()
      this.ensureCorrectTabVisible()
      this.restoreFilterValues()
      this.reinitializeFlatpickr()
    }, this.constructor.REINIT_DELAY)
  }

  applyTurboStreamAction (stream) {
    const action = stream.getAttribute('action')
    const target = stream.getAttribute('target')
    const template = stream.querySelector('template')

    if (!action || !target || !template) return

    const targetElement = document.getElementById(target)
    if (!targetElement) return

    const content = template.innerHTML

    switch (action) {
      case this.constructor.TURBO_ACTIONS.replace:
        targetElement.outerHTML = content
        break
      case this.constructor.TURBO_ACTIONS.update:
        targetElement.innerHTML = content
        break
      case this.constructor.TURBO_ACTIONS.append:
        targetElement.insertAdjacentHTML('beforeend', content)
        break
      case this.constructor.TURBO_ACTIONS.prepend:
        targetElement.insertAdjacentHTML('afterbegin', content)
        break
      case this.constructor.TURBO_ACTIONS.remove:
        targetElement.remove()
        break
    }
  }

  reinitializeControllers () {
    window.dispatchEvent(new Event('turbo:load'))
  }

  ensureCorrectTabVisible () {
    const activeTab = document.querySelector(this.constructor.SELECTORS.activeTab)
    if (!activeTab) return

    const tabType = activeTab.dataset.tabType
    const contentId = `${tabType}-sessions-content`
    const content = document.getElementById(contentId)

    if (content) {
      content.classList.remove('hidden')
    }
  }

  updateContent (html, tabType) {
    const parser = new DOMParser()
    const doc = parser.parseFromString(html, 'text/html')
    const contentId = `${tabType}-sessions-content`
    const newContent = doc.getElementById(contentId)
    const currentContent = document.getElementById(contentId)

    if (newContent && currentContent) {
      currentContent.innerHTML = newContent.innerHTML
    }

    setTimeout(() => {
      this.restoreFilterValues()
      this.reinitializeFlatpickr()
    }, this.constructor.REINIT_DELAY)
  }

  restoreFilterValues () {
    if (!this.lastFilterUrl) return

    const params = FilterUrlBuilder.parseParams(this.lastFilterUrl)
    if (!params) return

    this.restoreSessionType(params)
    this.restoreQuery(params)
    this.restoreDateRange(params)
    this.restoreTimeValues('from', params.get('from_time'))
    this.restoreTimeValues('to', params.get('to_time'))
  }

  restoreSessionType (params) {
    if (params.has('session_type') && this.hasSessionTypeTarget) {
      this.sessionTypeTarget.value = params.get('session_type')
    }
  }

  restoreQuery (params) {
    if (params.has('query') && this.hasQueryTarget) {
      this.queryTarget.value = params.get('query')
    }
  }

  restoreDateRange (params) {
    if (params.has('date_range') && this.hasDateRangeTarget) {
      const dateRangeValue = params.get('date_range')
      this.dateRangeTarget.value = dateRangeValue
    }
  }

  restoreTimeValues (prefix, time24) {
    if (!time24) return

    let parsed
    try {
      parsed = parse(time24, 'HH:mm', new Date())
      if (isNaN(parsed.getTime())) return
    } catch {
      return
    }

    const capitalized = prefix.charAt(0).toUpperCase() + prefix.slice(1)

    if (this[`has${capitalized}HourTarget`]) {
      this[`${prefix}HourTarget`].value = format(parsed, 'hh')
    }

    if (this[`has${capitalized}MinuteTarget`]) {
      this[`${prefix}MinuteTarget`].value = format(parsed, 'mm')
    }

    if (this[`has${capitalized}AmPmTarget`]) {
      this[`${prefix}AmPmTarget`].value = format(parsed, 'a').toUpperCase()
    }
  }

  showLoading () {
    this.setLoadingState(this.constructor.LOADING_OPACITY, 'none')
  }

  hideLoading () {
    this.setLoadingState('1', 'auto')
  }

  setLoadingState (opacity, pointerEvents) {
    const container = document.getElementById(this.constructor.SELECTORS.sessionsContainer)
    if (!container) return

    container.style.opacity = opacity
    container.style.pointerEvents = pointerEvents
  }
}
